import state from '@state';
import config from '@config';
import { showSpeechFeedback } from '@voice/feedback';
import { displayResponse, showLoading } from '@response/renderer';
import { log } from '@utils/logger';
import i18n from '@i18n';
import { smartFetch } from '@utils/smartFetch';
import { wait } from '@query/processor';

/**
 * Initializes the audio recording system with silence detection and debugging tools.
 *
 * @returns An object with `start` and `stop` methods for managing the recording, or `null` if unsupported.
 */
export function initAudioRecording(): {
	start: () => Promise<void>;
	stop: () => void;
} | null {
	if (!navigator.mediaDevices || !window.MediaRecorder) {
		console.error('[AgentEx] Audio recording not supported in this browser');
		return null;
	}

	let recognition: { lang: string; interimResults: boolean; continuous: boolean; onstart: () => void; onresult: (event: SpeechRecognitionEvent) => void; onerror: (event: Event & { error: string; }) => void; onend: () => void; start: () => void; stop: () => void; };
	let mediaStream: MediaStream | null = null;

	return {
		/**
		 * Starts the audio recording, monitors silence, and handles debug playback.
		 */
		start: async (): Promise<void> => {
			if (state.isRecording || state.mediaRecorder?.state === 'recording') {
				log('Recording already in progress — start() call ignored');
				return;
			}

			try {
				mediaStream = await navigator.mediaDevices.getUserMedia({
					audio: {
						channelCount: 1,
						echoCancellation: true,
						noiseSuppression: true,
						autoGainControl: true,
					},
				});

				const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

				if (SpeechRecognition) {
					recognition = new SpeechRecognition();
					recognition.lang = navigator.language || 'en';
					recognition.interimResults = true;
					recognition.continuous = true;

					recognition.onstart = () => {
						log('Speech recognition started');
					};

					recognition.onresult = (event: SpeechRecognitionEvent) => {
						let transcript = '';
						for (let i = event.resultIndex; i < event.results.length; ++i) {
							transcript += event.results[i][0].transcript;
						}

						updateSearchInput(transcript);
					};

					recognition.onerror = (event: Event & { error: string }) => {
						console.error('[AgentEx] Speech recognition error:', event.error);
					};

					recognition.onend = () => {
						log('Speech recognition ended');
					};

					recognition.start();
				} else {
					log('Live transcription is not supported in this browser.');
				}

				const mimeType = 'audio/webm';

				if (!MediaRecorder.isTypeSupported(mimeType)) {
					throw new Error('Audio recording not supported in this browser');
				}

				state.mediaRecorder = new MediaRecorder(mediaStream, {
					mimeType,
					audioBitsPerSecond: 128000,
				});

				state.audioFileExtension = 'webm';
				state.audioChunks = [];

				const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
				const source = audioContext.createMediaStreamSource(mediaStream);
				const analyser = audioContext.createAnalyser();
				analyser.fftSize = 512;
				source.connect(analyser);

				const bufferLength = analyser.frequencyBinCount;
				const dataArray = new Uint8Array(bufferLength);
				let silenceStart: number | null = null;
				let isSpeaking = false;
				const SILENCE_THRESHOLD = 15;
				const SILENCE_DURATION = 1500;

				const checkAudioLevel = (): void => {
					if (!state.isRecording) { return; }

					analyser.getByteFrequencyData(dataArray);
					const average = dataArray.reduce((a, b) => a + b) / bufferLength;

					if (average > SILENCE_THRESHOLD) {
						isSpeaking = true;
						silenceStart = null;
					} else if (isSpeaking) {
						if (!silenceStart) { silenceStart = Date.now(); }
						else if (Date.now() - silenceStart > SILENCE_DURATION) {
							if (state.mediaRecorder && state.isRecording) {
								state.mediaRecorder.stop();
								mediaStream?.getTracks().forEach((track) => track.stop());
								return;
							}
						}
					}

					requestAnimationFrame(checkAudioLevel);
				};

				state.mediaRecorder.ondataavailable = (event: BlobEvent): void => {
					if (event.data.size > 0) {
						state.audioChunks.push(event.data);
					}
				};

				state.mediaRecorder.onstart = (): void => {
					state.isRecording = true;
					isSpeaking = false;
					silenceStart = null;
					updateVoiceButtonState(true);
					showSpeechFeedback(i18n.t('recordingMessage'), 'info');
					requestAnimationFrame(checkAudioLevel);

					log(i18n.t('log.recordingStartedLog'), <any>{
						mimeType: state.mediaRecorder?.mimeType,
						audioBitsPerSecond: state.mediaRecorder?.audioBitsPerSecond,
						state: state.mediaRecorder?.state,
					});
				};

				state.mediaRecorder.onstop = async (): Promise<void> => {
					const audioBlob = new Blob(state.audioChunks, { type: 'audio/webm' });
					recognition?.stop();

					if (config.debug) {
						const playbackContainer = document.createElement('div');
						playbackContainer.className = 'aex-debug-playback';

						const audioUrl = URL.createObjectURL(audioBlob);
						const audio = document.createElement('audio');
						audio.controls = true;
						audio.preload = 'auto';

						const sourceEl = document.createElement('source');
						sourceEl.src = audioUrl;
						sourceEl.type = 'audio/webm;codecs=opus';
						audio.appendChild(sourceEl);

						const playButton = document.createElement('button');
						playButton.className = 'aex-play-button';
						playButton.innerHTML = `
							<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
								<path d="M8 5v14l11-7z"/>
							</svg>
							<span class="visually-hidden">Play audio</span>
						`;
						playButton.onclick = () => {
							if (audio.readyState >= 3) {
								audio.play();
							} else {
								audio.addEventListener('canplaythrough', () => audio.play(), { once: true });
								audio.load();
							}
						};

						playbackContainer.appendChild(audio);
						playbackContainer.appendChild(playButton);

						const sendButton = document.createElement('button');
						sendButton.className = 'aex-play-button';
						sendButton.classList.add('aex-send-button');
						sendButton.innerHTML = `
							<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
								<path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
							</svg>
						`;
						sendButton.onclick = async () => {
							playbackContainer.remove();
							URL.revokeObjectURL(audioUrl);
							await sendAudioToWebhook(audioBlob);
						};

						const cancelButton = document.createElement('button');
						cancelButton.className = 'aex-play-button';
						cancelButton.classList.add('aex-cancel-button');
						cancelButton.innerHTML = `
							<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
								<path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
							</svg>
						`;
						cancelButton.onclick = () => {
							playbackContainer.remove();
							URL.revokeObjectURL(audioUrl);
							state.audioChunks = [];
						};

						const label = document.createElement('span');
						label.className = 'aex-debug-label';
						label.textContent = 'Debug: Review Recording';

						playbackContainer.appendChild(label);
						playbackContainer.appendChild(sendButton);
						playbackContainer.appendChild(cancelButton);
						document.body.appendChild(playbackContainer);

						setTimeout(() => {
							if (document.body.contains(playbackContainer)) {
								playbackContainer.remove();
								URL.revokeObjectURL(audioUrl);
							}
						}, 30000);
					} else {
						updateVoiceButtonState(false);
						await sendAudioToWebhook(audioBlob);
					}

					// Remove feedback
					const feedback = document.querySelector('.aex-speech-feedback');

					if (feedback) {
						feedback.remove();
					}

					state.audioChunks = [];
					state.isRecording = false;
					updateVoiceButtonState(false);
				};

				state.mediaRecorder.start(100);
				showSpeechFeedback(i18n.t('labels.speechFeedback'), 'info');
			} catch (error) {
				console.error(i18n.t('error.recordingStartedError'), error);
				showSpeechFeedback(i18n.t('error.recordingMicrophoneError'), 'error');
			}
		},

		/**
		 * Stops the current recording session and releases the media stream.
		 */
		stop: (): void => {
			if (state.mediaRecorder && state.isRecording) {
				state.mediaRecorder.stop();
				mediaStream?.getTracks().forEach((track) => track.stop());
			}
		},
	};
}

/**
 * Sends the audio blob to the configured webhook as a voice search request.
 *
 * @param audioBlob - The recorded audio blob to send
 */
async function sendAudioToWebhook(audioBlob: Blob): Promise<void> {
	const queryId = Date.now().toString();

	showLoading();

	try {
		// Cancel any existing query
		state.abortManager.cancelCurrentRequest();
		let hasResult = false;

		// Create a new abort signal for this query
		const signal = state.abortManager.createAbortSignal();
		state.activeQueryId = queryId;

		const webmBlob = new Blob([audioBlob], { type: 'audio/webm' });

		const fd = new FormData();
		fd.append('audio_file', webmBlob, 'recording.webm');

		// Define the URLs.
		const webhookUrl = state.currentInstance?.webhookUrl || config.webhookUrl;
		let pollingUrl = config.pollingUrl;

		const response = await smartFetch(webhookUrl, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
			},
			body: fd,
			signal: signal,
		});

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		// Get the task ID.
		const results = await response.json();
		pollingUrl = pollingUrl.replace('{taskId}', results.id);

		// Define the count.
		let count = 0;

		// Max wait: 2 minutes.
		while (count <= 24) {
			const pResponse = await smartFetch(pollingUrl, {
				method: 'GET',
				signal: signal,
			});

			if (state.activeQueryId !== queryId) {
				log(i18n.t('log.querySuperceeded'));
				return;
			}

			if (!pResponse.ok) throw new Error(`${i18n.t('error.HTTPError')}: ${pResponse.status}`);

			const pResults = await pResponse.json();
			if (pResults.status === 'pending') {
				count++;
				await wait(5_000);
				continue;
			}

			hasResult = true;
			if (state.activeQueryId === queryId) displayResponse(pResults);
			break;
		}

		// If we got here, and don't have a result, we can assume it timed out.
		if (!hasResult) throw new Error(i18n.t('error.requestIssue'));

		// const data = await response.json();
		// displayResponse(data);
	} catch (error) {
		console.error('[AgentEx] Error sending audio:', error);
		displayResponse({
			error: 'Sorry, there was an error processing your audio. Please try again.',
		});
	}
}

/**
 * Updates the search input with the transcription
 * @param transcription - The transcribed text from the audio
 */
function updateSearchInput(transcription: string): void {
	const searchInput = document.querySelector<HTMLInputElement>(`${state.currentInstance?.searchForm} input[type="search"]`);
	if (searchInput) {
		searchInput.value = transcription;
		searchInput.dispatchEvent(new Event('input', { bubbles: true }));
		searchInput.dispatchEvent(new Event('change', { bubbles: true }));
		log('Search input updated with transcription: ' + transcription);
	}
}

/**
 * Toggles the visual state of the voice button when recording is active or stopped.
 *
 * @param isRecording - Whether recording is currently active
 */
function updateVoiceButtonState(isRecording: boolean): void {
	let voiceButton = null;

	if (state.currentInstance?.searchContainer) {
		voiceButton = document.querySelector<HTMLElement>(`${state.currentInstance?.searchContainer} .aex-voice-button`);
	} else {
		voiceButton = document.querySelector<HTMLElement>(`${state.currentInstance?.searchForm} .aex-voice-button`);
	}

	if (voiceButton) {
		voiceButton.classList.toggle('listening', isRecording);

		if (isRecording) {
			showSpeechFeedback(i18n.t('labels.speechFeedback'), 'info');
		} else {
			voiceButton.style.backgroundColor = '';
		}
	}
}
